import React from 'react';

import {Button} from '@deviceinsight/ng-ui-basic-components';

import {Trans, WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';
import EnumDropdown from '../../common/components/EnumDropdown';
import {createKpiChartItem, KpiQuery} from '../../common/kpiUtils';
import {KpiWidgetItem} from '../../common/types';
import {KpiPieChartWidgetConfig} from '../types';
import TimeConfig from '../../common/components/TimeConfig';
import SortableKpiItemConfigTable from '../../common/components/kpi-items/SortableKpiItemConfigTable';

type Props = {
	context: WidgetProps['context'];
	config: KpiPieChartWidgetConfig;
	onChangeConfig: (arg0: KpiPieChartWidgetConfig) => void;
	setSelectingAsset: (index: number) => void;
	setSelectingKpi: (index: number, assetUrn: string) => void;
};

const KpiPieChartWidgetConfigContent = ({
	context,
	config,
	config: {settings},
	onChangeConfig,
	setSelectingAsset,
	setSelectingKpi
}: Props) => {
	const asset = context.type === 'asset' ? context.asset : null;
	return (
		<div className="di form kpi-multi-widget-config">
			<div className="fields">
				<div className="field">
					<div className="standard wide field">
						<label>
							<Trans i18nKey="dashboard.widgets.config.boxHeadline">Headline</Trans>
						</label>
						<input
							data-testid="title-input"
							type="text"
							value={config.title}
							onChange={(e) => onChangeConfig({...config, title: e.target.value})}
						/>
					</div>
				</div>
			</div>
			<div className="fields">
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.aggregationtype">
							Aggregation type
						</Trans>
					</label>
					<EnumDropdown
						toFilter={['ALL']}
						data-testid="aggregation-type-select"
						options={KpiQuery}
						value={settings.aggregationType}
						onChange={(aggregationType) =>
							onChangeConfig({...config, settings: {...config.settings, aggregationType}})
						}
					/>
				</div>
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.unite">Unit</Trans>
					</label>
					<input
						type="text"
						data-testid="unit-input"
						value={settings.unit || ''}
						onChange={({target: {value}}) =>
							onChangeConfig({...config, settings: {...config.settings, unit: value}})
						}
					/>
				</div>
			</div>
			<TimeConfig
				periodTimeframe={settings.periodTimeframe}
				periodType={settings.periodType}
				periodCount={settings.periodCount}
				customPeriodFrom={settings.customPeriodFrom}
				customPeriodTo={settings.customPeriodTo}
				workingShiftEnabled={settings.workingShiftEnabled}
				workingShiftTimeFrom={settings.workingShiftTimeFrom}
				workingShiftTimeTo={settings.workingShiftTimeTo}
				workingShiftDays={settings.workingShiftDays}
				onPeriodTimeframePeriodTypeChange={(periodTimeframe, periodType) =>
					onChangeConfig({...config, settings: {...config.settings, periodTimeframe, periodType}})
				}
				onPeriodCountChange={(periodCount) =>
					onChangeConfig({...config, settings: {...config.settings, periodCount}})
				}
				onCustomPeriodChange={(customPeriodFrom, customPeriodTo) =>
					onChangeConfig({...config, settings: {...config.settings, customPeriodFrom, customPeriodTo}})
				}
				onWorkingShiftEnabledChange={(workingShiftEnabled) =>
					onChangeConfig({...config, settings: {...config.settings, workingShiftEnabled}})
				}
				onWorkingShiftTimeChange={(workingShiftTimeFrom, workingShiftTimeTo) =>
					onChangeConfig({
						...config,
						settings: {...config.settings, workingShiftTimeFrom, workingShiftTimeTo}
					})
				}
				onWorkingShiftDaysChange={(workingShiftDays) =>
					onChangeConfig({...config, settings: {...config.settings, workingShiftDays}})
				}
			/>
			<SortableKpiItemConfigTable
				items={settings.items}
				setSelectingKpi={setSelectingKpi}
				setSelectingAsset={setSelectingAsset}
				context={context}
				withUnit={false}
				withChartType={false}
				onItemChange={(index: number, item: KpiWidgetItem) => {
					onChangeConfig({
						...config,
						settings: {
							...settings,
							items: [...settings.items.slice(0, index), item, ...settings.items.slice(index + 1)]
						}
					});
				}}
				onItemRemove={(index: number) => {
					onChangeConfig({
						...config,
						settings: {
							...settings,
							items: [...settings.items.slice(0, index), ...settings.items.slice(index + 1)]
						}
					});
				}}
				onOrderChange={(items) =>
					onChangeConfig({
						...config,
						// eslint-disable-next-line @typescript-eslint/no-unused-vars
						settings: {...config.settings, items}
					})
				}
			/>
			<div className="fields">
				<div className="field">
					<Button
						onClick={() =>
							onChangeConfig({
								...config,
								settings: {
									...config.settings,
									items: [...config.settings.items, createKpiChartItem(asset, undefined, undefined)]
								}
							})
						}>
						<Trans i18nKey="dashboard.widgets.kpiaggredatedchart.addKpi">Add Kpi</Trans>
					</Button>
				</div>
			</div>
		</div>
	);
};

export default KpiPieChartWidgetConfigContent;
